import { useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Page, Table, Pagination, Loader, PageTitleWithButton } from '../../../components';
import { Button, useMediaQuery } from '@mui/material';
import { Edit, Add } from '@mui/icons-material';
import { getMunicipalities, getMunicipality } from '../../../fetch';
import { IMunicipality } from '../../../models';
import { MobileMunicipality } from './MobileMunicipalities';
import { MunicipalityModal } from './MunicipalityModal';

export const ManageMunicipalities = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [municipalities, setMunicipalities] = useState<IMunicipality[]>([]);
  const [isLoadingMunicipalities, setLoadingMunicipalities] = useState<boolean>(true);
  const [isLoadingMunicipality, setLoadingMunicipality] = useState<boolean>(false);
  const [isModalOpen, showModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [currentMunicipality, setCurrentMunicipality] = useState<any>(null);

  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchMunicipalities = async () => {
    try {
      setLoadingMunicipalities(true);
      const res = await getMunicipalities({ page: page + 1, perPage: perPage });
      setRecordCount(res.totalRecordCount);
      setMunicipalities(res.records);
    } catch (error) {
      enqueueSnackbar(`Error loading municipalities, please try again.`, {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingMunicipalities(false);
    }
  };

  const handleEdit = async (id: number) => {
    setLoadingMunicipality(true);
    try {
      const res = await getMunicipality(id);
      setCurrentMunicipality(res);
      showModal(true);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? `Error loading municipality, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoadingMunicipality(false);
    }
  };

  useEffect(() => {
    fetchMunicipalities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        sort: false
      },
      {
        Header: 'State',
        accessor: 'state',
        sort: false
      },
      {
        Header: 'County',
        accessor: 'county',
        sort: false
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        overrideWidth: 200,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IMunicipality } };
        }) => {
          return (
            <>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                onClick={async () => {
                  handleEdit(original.municipalityId);
                }}
              >
                Edit
              </Button>
            </>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [municipalities]);
  return (
    <>
      {isLoadingMunicipality && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Page title="Manage Municipalities">
        <PageTitleWithButton
          pageTitleBackProps={{
            showDivider: false,
            backText: 'Back to Admin home',
            backUrl: '/admin',
            title: 'Manage Municipalities'
          }}
          button={
            <Button
              startIcon={<Add />}
              onClick={() => showModal(true)}
              color="primary"
              variant="contained"
            >
              Add Municipality
            </Button>
          }
        />
        <Table
          columns={columns}
          data={municipalities}
          isLoading={isLoadingMunicipalities}
          hidePagination
          mobileProps={{
            handleEdit: (val: IMunicipality) => {
              handleEdit(val.municipalityId);
            }
          }}
          ResponsiveComponent={isMobile ? MobileMunicipality : undefined}
        />
        {!isLoadingMunicipalities && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
        <MunicipalityModal
          currentMunicipality={currentMunicipality}
          open={isModalOpen}
          onClose={() => {
            showModal(false);
            setTimeout(() => {
              setCurrentMunicipality(null);
            }, 500);
          }}
          fetchMunicipalities={fetchMunicipalities}
        />
      </Page>
    </>
  );
};
